// ============================================================
// Supplier Feature Catalog — Central constants & types
// ============================================================
// This file defines the supplier-only feature keys and types
// used by the entitlement system. Customer features are NOT
// gated by this system — customers always have full access.
// ============================================================

export const FEATURE_KEYS = {
  MARKETPLACE_INQUIRIES: "marketplace_inquiries_access",
  MATERIAL_INQUIRY: "material_inquiry",
  CUSTOM_DOMAIN: "custom_domain",
  PUBLIC_CONTACTS: "public_contacts",
  CHATBOT_PROMO: "supplier_chatbot_promo",
  ADVANCED_PRICING: "advanced_pricing",
} as const;

export type FeatureKey = (typeof FEATURE_KEYS)[keyof typeof FEATURE_KEYS];

export type FallbackMode = "hidden" | "locked" | "read_only";

export type EntitlementStatus = "allowed" | "locked" | "hidden" | "read_only";

export type EntitlementReason =
  | "plan"
  | "override_grant"
  | "override_deny"
  | "missing_service"
  | "missing_plugin"
  | "no_plan"
  | "bridge";

export interface FeatureEntitlement {
  key: string;
  label: string;
  status: EntitlementStatus;
  fallbackMode: FallbackMode;
  reason: EntitlementReason;
}

export interface SupplierEntitlements {
  planKey: string | null;
  planName: string | null;
  features: Record<string, FeatureEntitlement>;
  isLoading: boolean;
  canUse: (featureKey: string) => boolean;
  getFeature: (featureKey: string) => FeatureEntitlement | null;
}

export interface SubscriptionPlan {
  id: string;
  key: string;
  name: string;
  description: string | null;
  sort_order: number;
  is_active: boolean;
}

export interface SubscriptionFeature {
  id: string;
  key: string;
  label: string;
  category: string;
  description: string | null;
  fallback_mode: FallbackMode;
  required_service: string | null;
  required_plugin: string | null;
  is_active: boolean;
  sort_order: number;
}

export interface SupplierSubscription {
  id: string;
  supplier_id: string;
  plan_id: string;
  activated_at: string;
  activated_by: string | null;
  expires_at: string | null;
  is_active: boolean;
  notes: string | null;
}

export interface SupplierFeatureOverride {
  id: string;
  supplier_id: string;
  feature_key: string;
  is_enabled: boolean;
  reason: string | null;
}

// Feature category labels for UI
export const FEATURE_CATEGORIES: Record<string, string> = {
  core: "Základní",
  pricing: "Cenotvorba",
  branding: "Branding",
  communication: "Komunikace",
  general: "Obecné",
};
